import React, { useEffect, useMemo, useState, memo, Component } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { Tooltip } from '../components/ui/Tooltip';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { ProgressiveNumberInput } from '../components/ProgressiveNumberInput';
import { mockData } from '../services/mockData';
import { ArrowLeft, Edit, Trash2, ClipboardList, Ticket, Plus, Phone, Mail, Globe, MapPin, Building2, Users, FileText, Receipt, AlertTriangle, Wrench, GraduationCap, Save, X, Check, ChevronRight, User, Smartphone, Shield, Info, Calendar, Eye, Clock, HardHat, TrendingUp, Hammer, AlertCircle, CheckCircle2, Stethoscope, ExternalLink, Briefcase, Lightbulb, Euro, Award, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClientSectionPage } from './ClientSectionPage';
import { ClientStatusBanner } from '../components/ClientStatusBanner';
import { Kanban } from '../components/ui/Kanban';
import { Timeline } from '../components/ui/Timeline';
import { actionsStore, sortActions } from '../services/actionsStore';
import { ActionsTable } from '../components/operations/ActionsTable';
// Mock Data for the Detail Page
const zones = ['Arezzo', 'Casentino', 'Valdarno', 'Valdichiana', 'Valtiberina - Perugia', 'Siena', 'Montalcino', 'Firenze – Prato', 'Grosseto', 'Pisa – Livorno – Lucca – Massa Carrara'];
const summarySections = [{
  id: 'workers',
  title: 'Lavoratori',
  icon: HardHat,
  count: 12
}, {
  id: 'deadlines',
  title: 'Scadenze',
  icon: Calendar,
  count: 3
}, {
  id: 'contracts',
  title: 'Offerte e contratti',
  icon: FileText,
  count: 5
}, {
  id: 'invoices',
  title: 'Fatturazione',
  icon: Receipt,
  count: 8
}, {
  id: 'training',
  title: 'Formazione',
  icon: GraduationCap,
  count: 9
}, {
  id: 'operations',
  title: 'Attività operative',
  icon: ClipboardList,
  count: 4
}, {
  id: 'non_conformities',
  title: 'Non conformità',
  icon: AlertTriangle,
  count: 2
}, {
  id: 'improvements',
  title: 'Miglioramenti',
  icon: TrendingUp,
  count: 3
}, {
  id: 'equipment',
  title: 'Macchinari',
  icon: Wrench,
  count: 15
}, {
  id: 'maintenance',
  title: 'Manutenzione',
  icon: Hammer,
  count: 6
}];
// Helper to generate realistic mock data based on section
const getSectionData = (sectionId: string) => {
  switch (sectionId) {
    case 'workers':
      return [{
        id: 1,
        col1: 'Mario Rossi',
        col2: 'Operaio',
        col3: 'Attivo',
        col4: '12/05/2024'
      }, {
        id: 2,
        col1: 'Luigi Verdi',
        col2: 'Impiegato',
        col3: 'Attivo',
        col4: '10/01/2024'
      }, {
        id: 3,
        col1: 'Anna Bianchi',
        col2: 'Tecnico',
        col3: 'In scadenza',
        col4: '15/06/2024'
      }];
    case 'deadlines':
      return [{
        id: 1,
        col1: 'Rinnovo DVR',
        col2: 'Sicurezza',
        col3: 'In scadenza',
        col4: '15/06/2024'
      }, {
        id: 2,
        col1: 'Analisi acque',
        col2: 'Ambiente',
        col3: 'Programmata',
        col4: '20/06/2024'
      }, {
        id: 3,
        col1: 'Verifica estintori',
        col2: 'Antincendio',
        col3: 'Programmata',
        col4: '30/06/2024'
      }];
    case 'contracts':
      return [{
        id: 1,
        col1: 'CTR-2024-001',
        col2: 'Sicurezza Annuale',
        col3: 'Attivo',
        col4: '01/01/2024'
      }, {
        id: 2,
        col1: 'CTR-2024-005',
        col2: 'HACCP Base',
        col3: 'In scadenza',
        col4: '15/02/2024'
      }, {
        id: 3,
        col1: 'PREV-2024-012',
        col2: 'Formazione',
        col3: 'Bozza',
        col4: '10/05/2024'
      }];
    case 'invoices':
      return [{
        id: 1,
        col1: 'Fatt. 45/2024',
        col2: 'Canone annuale',
        col3: 'Pagata',
        col4: '15/04/2024'
      }, {
        id: 2,
        col1: 'Fatt. 52/2024',
        col2: 'Corso formazione',
        col3: 'In attesa',
        col4: '10/05/2024'
      }, {
        id: 3,
        col1: 'Proforma 12',
        col2: 'Consulenza',
        col3: 'Bozza',
        col4: '20/05/2024'
      }];
    case 'operations':
      return [{
        id: 1,
        col1: 'Sopralluogo',
        col2: 'Sicurezza',
        col3: 'Completato',
        col4: '10/05/2024'
      }, {
        id: 2,
        col1: 'Verifica',
        col2: 'Macchinari',
        col3: 'Programmato',
        col4: '25/05/2024'
      }, {
        id: 3,
        col1: 'Audit',
        col2: 'Qualità',
        col3: 'In corso',
        col4: '15/05/2024'
      }];
    case 'non_conformities':
      return [{
        id: 1,
        col1: 'NC-2024-01',
        col2: 'Mancanza DPI',
        col3: 'Aperta',
        col4: '12/04/2024'
      }, {
        id: 2,
        col1: 'NC-2024-03',
        col2: 'Documentazione',
        col3: 'Risolta',
        col4: '05/05/2024'
      }, {
        id: 3,
        col1: 'NC-2024-04',
        col2: 'Segnaletica',
        col3: 'Aperta',
        col4: '15/05/2024'
      }];
    case 'improvements':
      return [{
        id: 1,
        col1: 'IMP-001',
        col2: 'Digitalizzazione',
        col3: 'In valutazione',
        col4: '01/03/2024'
      }, {
        id: 2,
        col1: 'IMP-002',
        col2: 'Formazione extra',
        col3: 'Approvato',
        col4: '15/04/2024'
      }, {
        id: 3,
        col1: 'IMP-003',
        col2: 'Nuovi DPI',
        col3: 'Completato',
        col4: '10/01/2024'
      }];
    case 'equipment':
      return [{
        id: 1,
        col1: 'Carrello elevatore',
        col2: 'Logistica',
        col3: 'Conforme',
        col4: '12/02/2024'
      }, {
        id: 2,
        col1: 'Pressa idraulica',
        col2: 'Produzione',
        col3: 'Manutenzione',
        col4: '10/05/2024'
      }, {
        id: 3,
        col1: 'Trapano a colonna',
        col2: 'Officina',
        col3: 'Conforme',
        col4: '15/03/2024'
      }];
    case 'maintenance':
      return [{
        id: 1,
        col1: 'Ordinaria',
        col2: 'Carrello A',
        col3: 'Eseguita',
        col4: '01/04/2024'
      }, {
        id: 2,
        col1: 'Straordinaria',
        col2: 'Pressa B',
        col3: 'In corso',
        col4: '15/05/2024'
      }, {
        id: 3,
        col1: 'Periodica',
        col2: 'Impianto elettrico',
        col3: 'Programmata',
        col4: '20/06/2024'
      }];
    case 'training':
      return [{
        id: 1,
        col1: 'Sicurezza Gen.',
        col2: 'Base',
        col3: 'Valido',
        col4: '12/05/2024'
      }, {
        id: 2,
        col1: 'Primo Soccorso',
        col2: 'Emergenza',
        col3: 'In scadenza',
        col4: '15/06/2024'
      }, {
        id: 3,
        col1: 'RLS',
        col2: 'Specifico',
        col3: 'Scaduto',
        col4: '01/01/2024'
      }];
    default:
      return [];
  }
};
// ... (Keep other helpers and mock data same as original)
const getSectionColumns = (sectionId: string) => {
  switch (sectionId) {
    case 'workers':
      return ['Nominativo', 'Mansione', 'Stato', 'Data assunzione'];
    case 'deadlines':
      return ['Titolo', 'Ambito', 'Stato', 'Scadenza'];
    case 'contracts':
      return ['Codice', 'Oggetto', 'Stato', 'Data stipula'];
    case 'invoices':
      return ['Numero', 'Descrizione', 'Stato', 'Data'];
    case 'operations':
      return ['Attività', 'Tipo', 'Stato', 'Data'];
    case 'non_conformities':
      return ['Codice', 'Oggetto', 'Stato', 'Data apertura'];
    case 'improvements':
      return ['Codice', 'Oggetto', 'Stato', 'Data proposta'];
    case 'equipment':
      return ['Macchinario', 'Reparto', 'Stato', 'Ultima verifica'];
    case 'maintenance':
      return ['Tipo', 'Oggetto', 'Stato', 'Data'];
    case 'training':
      return ['Corso', 'Tipo', 'Stato', 'Data corso'];
    default:
      return ['Riferimento', 'Descrizione', 'Stato', 'Data'];
  }
};
export function ClientDetailPage() {
  const {
    id,
    section
  } = useParams<{
    id: string;
    section?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(section || null);
  const [refresh, setRefresh] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const [showTicketHistory, setShowTicketHistory] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showNewOfferModal, setShowNewOfferModal] = useState(false);
  const [offerSaveSuccess, setOfferSaveSuccess] = useState(false);
  const isPending = searchParams.get('pending') === 'true';
  const clientActions = useMemo(() => {
    if (!client || isPending) return [];
    return sortActions(actionsStore.getByClient(client.name));
  }, [client, refresh, isPending]);
  const handleActionUpdate = () => {
    setRefresh((r: number) => r + 1);
  };
  // Sync section state with URL
  useEffect(() => {
    // Check for edit mode param
    if (searchParams.get('edit') === 'true') {
      setIsEditing(true);
    }
    // Simulate fetch
    const foundClient = mockData.getClients().find(c => c.id === Number(id)) || mockData.getClients()[0];
    // Enrich with extra fields required for this view
    const enrichedClient = {
      ...foundClient,
      piva: '01234567890',
      cf: 'ABCDE12345F',
      ateco: '43.21.01',
      address: 'Via Roma 15, 52100 Arezzo (AR)',
      legalAddress: 'Via Roma 15, 52100 Arezzo (AR)',
      operationalAddresses: ['Via Firenze 8, 52100 Arezzo (AR)', 'Via Casentino 3, 52010 Bibbiena (AR)'],
      legalRep: 'Mario Rossi',
      origin: 'Commerciale SEF',
      accountant: 'Studio Bianchi & Associati',
      activity: 'Costruzioni edili e ristrutturazioni',
      employees: 25,
      employeesCensused: 12,
      newsletter: true,
      clientOf: ['SEF Studio', 'SEFORM'],
      // Contacts
      companyPhone: '0575 123456',
      adminEmail: 'amministrazione@azienda.it',
      consultingEmail: 'consulenza@azienda.it',
      pec: 'azienda@pec.it',
      sdi: 'M5UXCR1',
      contactPerson: 'Giulia Verdi',
      contactMobile: '333 1234567',
      safetyReferent: 'Mario Rossi',
      safetyMobile: '335 9876543',
      trainingEmail: 'formazione@azienda.it',
      infoEmail: 'info@azienda.it',
      newsletterEmail: 'news@azienda.it'
    };
    setClient(enrichedClient);
    setFormData(enrichedClient);
    setLoading(false);
  }, [id, searchParams]);
  if (loading) return <Layout data-id="element-1236">
        <div className="p-8" data-id="element-1237">Caricamento...</div>
      </Layout>;
  if (!client) return <Layout data-id="element-1238">
        <div className="p-8" data-id="element-1239">Cliente non trovato</div>
      </Layout>;
  const handleSave = () => {
    setClient(formData);
    setIsEditing(false);
    if (searchParams.get('edit')) {
      navigate(`/clients/${id}`, {
        replace: true
      });
    }
  };
  // ... (Keep other handlers)
  const handleDeleteItem = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setDeleteItem(null);
    }, 1500);
  };
  const handleSaveItem = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setEditItem(null);
    }, 1500);
  };
  const handleView = (item: any) => {
    setViewItem(item);
    // Reset specific states
    setWorkerTab('info');
    setNewCourses([]);
    setNewVisits([]);
  };
  const handleEditItem = (item: any) => {
    setEditItem(item);
    // Reset specific states
    setRecurrenceMode('single');
    setMultipleDates(['']);
  };
  // Listing View Component
  if (activeSection) {
    return <ClientSectionPage sectionId={activeSection} clientName={client.name} onBack={() => setActiveSection(null)} data-id="element-1240" />;
  }
  return <Layout data-id="element-1241">
      {/* Breadcrumb */}
      <div className="mb-6" data-id="element-1242">
        <button onClick={() => navigate('/clients')} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-seform-navy transition-colors group" data-id="element-1243">
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" data-id="element-1244" />
          <span data-id="element-1245">Anagrafiche clienti</span>
        </button>
      </div>

      {/* Header */}
      <div className="pb-6 border-b border-slate-200 mb-8" data-id="element-1246">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4" data-id="element-1247">
          <div data-id="element-1248">
            <div className="flex items-center gap-3 mb-2" data-id="element-1249">
              <h1 className="text-3xl font-bold text-seform-navy" data-id="element-1250">
                {client.name}
              </h1>
              <StatusBadge status="Attivo" data-id="element-1251" />
            </div>
            <p className="text-slate-500 font-mono" data-id="element-1252">
              N° Cliente: CLI-{client.id.toString().padStart(4, '0')}
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1253">
            <Button variant="flat" startContent={<Ticket className="h-4 w-4" data-id="element-1255" />} onClick={() => setShowTicketHistory(true)} data-id="element-1254">
              Ticket
            </Button>
            <Button color={isEditing ? 'success' : 'secondary'} startContent={isEditing ? <Save className="h-4 w-4" data-id="element-1257" /> : <Edit className="h-4 w-4" data-id="element-1258" />} onClick={() => isEditing ? handleSave() : setIsEditing(true)} data-id="element-1256">
              {isEditing ? 'Salva modifiche' : 'Modifica'}
            </Button>
            <Button variant="flat" color="danger" startContent={<Trash2 className="h-4 w-4" data-id="element-1260" />} data-id="element-1259">
              Elimina
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8" data-id="element-1261">
        {/* Left Column */}
        <div className="xl:col-span-2 space-y-8" data-id="element-1262">
          {/* Client Data Card */}
          <Card title="Dati aziendali" className="pb-2" data-id="element-1263">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 pb-6" data-id="element-1264">
              {/* Cliente di */}
              <div className="col-span-full" data-id="element-1265">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1266">
                  Cliente di
                </label>
                {isEditing ? <div className="flex gap-4" data-id="element-1267">
                    <label className="flex items-center gap-2" data-id="element-1268">
                      <input type="checkbox" checked={formData.clientOf.includes('SEF Studio')} onChange={e => {
                    const newSet = e.target.checked ? [...formData.clientOf, 'SEF Studio'] : formData.clientOf.filter((x: string) => x !== 'SEF Studio');
                    setFormData({
                      ...formData,
                      clientOf: newSet
                    });
                  }} data-id="element-1269" />{' '}
                      SEF Studio
                    </label>
                    <label className="flex items-center gap-2" data-id="element-1270">
                      <input type="checkbox" checked={formData.clientOf.includes('SEFORM')} onChange={e => {
                    const newSet = e.target.checked ? [...formData.clientOf, 'SEFORM'] : formData.clientOf.filter((x: string) => x !== 'SEFORM');
                    setFormData({
                      ...formData,
                      clientOf: newSet
                    });
                  }} data-id="element-1271" />{' '}
                      SEFORM
                    </label>
                  </div> : <div className="flex gap-2" data-id="element-1272">
                    {client.clientOf.map((c: string) => <Badge key={c} variant="primary" data-id="element-1273">
                        {c}
                      </Badge>)}
                  </div>}
              </div>

              {/* P.IVA & CF */}
              <div data-id="element-1274">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1275">
                  P.IVA / Codice Fiscale
                </label>
                {isEditing ? <div className="flex gap-2" data-id="element-1276">
                    <Input value={formData.piva} onChange={e => setFormData({
                  ...formData,
                  piva: e.target.value
                })} placeholder="P.IVA" data-id="element-1277" />
                    <Input value={formData.cf} onChange={e => setFormData({
                  ...formData,
                  cf: e.target.value
                })} placeholder="CF" data-id="element-1278" />
                  </div> : <p className="font-medium text-slate-900" data-id="element-1279">
                    {client.piva} / {client.cf}
                  </p>}
              </div>

              {/* Ateco */}
              <div data-id="element-1280">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1281">
                  Codice Ateco
                </label>
                {isEditing ? <Input value={formData.ateco} onChange={e => setFormData({
                ...formData,
                ateco: e.target.value
              })} data-id="element-1282" /> : <p className="font-medium text-slate-900" data-id="element-1283">{client.ateco}</p>}
              </div>

              {/* Zona */}
              <div data-id="element-1284">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1285">
                  Zona
                </label>
                {isEditing ? <Select value={formData.zone} onChange={e => setFormData({
                ...formData,
                zone: e.target.value
              })} options={zones.map(z => ({
                value: z,
                label: z
              }))} data-id="element-1286" /> : <p className="font-medium text-slate-900" data-id="element-1287">{client.zone}</p>}
              </div>

              {/* Attività Aziendale - MOVED HERE (Right column, below Ateco) */}
              <div data-id="element-1288">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1289">
                  Attività Aziendale
                </label>
                {isEditing ? <Input value={formData.activity} onChange={e => setFormData({
                ...formData,
                activity: e.target.value
              })} data-id="element-1290" /> : <p className="font-medium text-slate-900" data-id="element-1291">
                    {client.activity}
                  </p>}
              </div>

              {/* Indirizzo */}
              <div data-id="element-1292">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1293">
                  Indirizzo
                </label>
                {isEditing ? <Input value={formData.address} onChange={e => setFormData({
                ...formData,
                address: e.target.value
              })} data-id="element-1294" /> : <p className="font-medium text-slate-900" data-id="element-1295">{client.address}</p>}
              </div>

              {/* Sede Legale */}
              <div data-id="element-1296">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1297">
                  Sede Legale
                </label>
                {isEditing ? <Input value={formData.legalAddress} onChange={e => setFormData({
                ...formData,
                legalAddress: e.target.value
              })} data-id="element-1298" /> : <p className="font-medium text-slate-900" data-id="element-1299">
                    {client.legalAddress}
                  </p>}
              </div>

              {/* Sede Operativa */}
              <div data-id="element-1300">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1301">
                  Sedi Operative
                </label>
                {isEditing ? <div className="space-y-2" data-id="element-1302">
                    {formData.operationalAddresses.map((addr: string, idx: number) => <div key={idx} className="flex gap-2" data-id="element-1303">
                          <Input value={addr} onChange={e => {
                    const newAddrs = [...formData.operationalAddresses];
                    newAddrs[idx] = e.target.value;
                    setFormData({
                      ...formData,
                      operationalAddresses: newAddrs
                    });
                  }} data-id="element-1304" />
                          <Button size="sm" variant="flat" color="danger" onClick={() => {
                    const newAddrs = formData.operationalAddresses.filter((_: any, i: number) => i !== idx);
                    setFormData({
                      ...formData,
                      operationalAddresses: newAddrs
                    });
                  }} data-id="element-1305">
                            <X className="h-4 w-4" data-id="element-1306" />
                          </Button>
                        </div>)}
                    <Button size="sm" variant="flat" onClick={() => setFormData({
                  ...formData,
                  operationalAddresses: [...formData.operationalAddresses, '']
                })} data-id="element-1307">
                      Aggiungi sede
                    </Button>
                  </div> : <div className="space-y-1" data-id="element-1308">
                    {client.operationalAddresses.map((addr: string, idx: number) => <p key={idx} className="font-medium text-slate-900" data-id="element-1309">
                          {addr}
                        </p>)}
                  </div>}
              </div>

              {/* Legale Rappresentante */}
              <div data-id="element-1310">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1311">
                  Legale Rappresentante
                </label>
                {isEditing ? <Input value={formData.legalRep} onChange={e => setFormData({
                ...formData,
                legalRep: e.target.value
              })} data-id="element-1312" /> : <p className="font-medium text-slate-900" data-id="element-1313">
                    {client.legalRep}
                  </p>}
              </div>

              {/* Provenienza */}
              <div data-id="element-1314">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1315">
                  Provenienza
                </label>
                {isEditing ? <Input value={formData.origin} onChange={e => setFormData({
                ...formData,
                origin: e.target.value
              })} data-id="element-1316" /> : <p className="font-medium text-slate-900" data-id="element-1317">{client.origin}</p>}
              </div>

              {/* Commercialista */}
              <div data-id="element-1318">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1319">
                  Commercialista
                </label>
                {isEditing ? <Input value={formData.accountant} onChange={e => setFormData({
                ...formData,
                accountant: e.target.value
              })} data-id="element-1320" /> : <p className="font-medium text-slate-900" data-id="element-1321">
                    {client.accountant}
                  </p>}
              </div>

              {/* Dipendenti */}
              <div data-id="element-1322">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1323">
                  Dipendenti
                </label>
                <div className="flex gap-4 items-center" data-id="element-1324">
                  <div data-id="element-1325">
                    <span className="text-xs text-slate-500 mr-2" data-id="element-1326">Totale:</span>
                    {isEditing ? <input type="number" className="w-16 border rounded p-1" value={formData.employees} onChange={e => setFormData({
                    ...formData,
                    employees: parseInt(e.target.value)
                  })} data-id="element-1327" /> : <span className="font-bold text-slate-900" data-id="element-1328">
                        {client.employees}
                      </span>}
                  </div>
                  <div data-id="element-1329">
                    <span className="text-xs text-slate-500 mr-2" data-id="element-1330">
                      Censiti:
                    </span>
                    {isEditing ? <input type="number" className="w-16 border rounded p-1" value={formData.employeesCensused} onChange={e => setFormData({
                    ...formData,
                    employeesCensused: parseInt(e.target.value)
                  })} data-id="element-1331" /> : <span className="font-bold text-seform-navy" data-id="element-1332">
                        {client.employeesCensused}
                      </span>}
                  </div>
                </div>
              </div>

              {/* Newsletter */}
              <div data-id="element-1333">
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-1" data-id="element-1334">
                  Iscrizione Newsletter
                </label>
                {isEditing ? <div className="flex items-center gap-2" data-id="element-1335">
                    <span className={formData.newsletter ? 'text-green-600 font-bold' : 'text-slate-400'} data-id="element-1336">
                      Sì
                    </span>
                    <button onClick={() => setFormData({
                  ...formData,
                  newsletter: !formData.newsletter
                })} className={`w-10 h-5 rounded-full relative transition-colors ${formData.newsletter ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-1337">
                      <div className={`w-3 h-3 bg-white rounded-full absolute top-1 transition-all ${formData.newsletter ? 'left-6' : 'left-1'}`} data-id="element-1338" />
                    </button>
                    <span className={!formData.newsletter ? 'text-red-600 font-bold' : 'text-slate-400'} data-id="element-1339">
                      No
                    </span>
                  </div> : <Badge variant={client.newsletter ? 'success' : 'danger'} data-id="element-1340">
                    {client.newsletter ? 'Sì' : 'No'}
                  </Badge>}
              </div>
            </div>
          </Card>

          {/* Azioni Card */}
          <Card title="Azioni" noPadding data-id="element-1341">
            {isPending ? <div className="flex flex-col items-center justify-center py-12 text-slate-400" data-id="element-1342">
                <ClipboardList className="h-10 w-10 mb-3 text-slate-300" data-id="element-1343" />
                <p className="text-sm font-medium" data-id="element-1344">
                  Nessuna azione disponibile
                </p>
                <p className="text-xs mt-1" data-id="element-1345">
                  Le azioni saranno disponibili dopo l'attivazione del cliente.
                </p>
              </div> : <div className="max-h-[400px] overflow-y-auto custom-scrollbar" data-id="element-1346">
                <ActionsTable actions={clientActions} onActionUpdate={handleActionUpdate} data-id="element-1347" />
              </div>}
          </Card>

          {/* Summary Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1348">
            {summarySections.map(sec => {
            const isPendingContracts = isPending && sec.id === 'contracts';
            return <Card key={sec.id} title={<div className="flex items-center gap-2" data-id="element-1350">
                      <sec.icon className="h-5 w-5 text-seform-navy" data-id="element-1351" />
                      <span className="text-seform-navy" data-id="element-1352">{sec.title}</span>
                    </div>} actions={isPendingContracts ? undefined : <Button size="sm" variant="ghost" onClick={() => setActiveSection(sec.id)} data-id="element-1353">
                        Vedi tutti
                      </Button>} data-id="element-1349">
                  {isPendingContracts ? <div className="flex flex-col items-center justify-center py-8" data-id="element-1354">
                      <FileText className="h-10 w-10 mb-3 text-slate-300" data-id="element-1355" />
                      <p className="text-sm text-slate-400 mb-4" data-id="element-1356">
                        Nessuna offerta o contratto presente.
                      </p>
                      <Button size="sm" startContent={<Plus className="h-4 w-4" data-id="element-1358" />} onClick={() => setShowNewOfferModal(true)} data-id="element-1357">
                        Nuova offerta
                      </Button>
                    </div> : <div className="space-y-3" data-id="element-1359">
                      {getSectionData(sec.id).map(row => <div key={row.id} className="flex justify-between items-center text-sm py-1 border-b border-slate-50 last:border-0" data-id="element-1360">
                          <span className="text-slate-700 truncate max-w-[70%]" data-id="element-1361">
                            {row.col1}
                          </span>
                          <Badge size="sm" variant={row.col3 === 'Attivo' || row.col3 === 'Completato' || row.col3 === 'Valido' || row.col3 === 'Pagata' || row.col3 === 'Risolta' || row.col3 === 'Conforme' || row.col3 === 'Approvato' || row.col3 === 'Eseguita' ? 'success' : row.col3 === 'In scadenza' || row.col3 === 'In attesa' || row.col3 === 'In corso' || row.col3 === 'In valutazione' || row.col3 === 'Manutenzione' ? 'warning' : row.col3 === 'Scaduto' || row.col3 === 'Aperta' ? 'danger' : 'neutral'} data-id="element-1362">
                            {row.col3}
                          </Badge>
                        </div>)}
                    </div>}
                </Card>;
          })}
          </div>
        </div>

        {/* Right Column - Contacts */}
        <div className="space-y-6" data-id="element-1363">
          <Card title="Contatti" data-id="element-1364">
            <div className="space-y-7" data-id="element-1365">
              {' '}
              {/* Increased vertical spacing */}
              {/* Telefono Aziendale */}
              <div className="flex items-start gap-3" data-id="element-1366">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1367">
                  <Phone className="h-4 w-4 text-slate-500" data-id="element-1368" />
                </div>
                <div className="flex-1" data-id="element-1369">
                  <p className="text-xs text-slate-500" data-id="element-1370">Telefono aziendale</p>
                  {isEditing ? <Input value={formData.companyPhone} onChange={e => setFormData({
                  ...formData,
                  companyPhone: e.target.value
                })} className="mt-1" data-id="element-1371" /> : <p className="font-medium text-slate-900" data-id="element-1372">
                      {client.companyPhone}
                    </p>}
                </div>
              </div>
              {/* Email Amministrativa */}
              <div className="flex items-start gap-3" data-id="element-1373">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1374">
                  <Mail className="h-4 w-4 text-slate-500" data-id="element-1375" />
                </div>
                <div className="flex-1" data-id="element-1376">
                  <p className="text-xs text-slate-500" data-id="element-1377">Email amministrativa</p>
                  {isEditing ? <Input value={formData.adminEmail} onChange={e => setFormData({
                  ...formData,
                  adminEmail: e.target.value
                })} className="mt-1" data-id="element-1378" /> : <p className="font-medium text-slate-900 break-all" data-id="element-1379">
                      {client.adminEmail}
                    </p>}
                </div>
              </div>
              {/* Email Consulenza */}
              <div className="flex items-start gap-3" data-id="element-1380">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1381">
                  <Mail className="h-4 w-4 text-slate-500" data-id="element-1382" />
                </div>
                <div className="flex-1" data-id="element-1383">
                  <p className="text-xs text-slate-500" data-id="element-1384">Email consulenza</p>
                  {isEditing ? <Input value={formData.consultingEmail} onChange={e => setFormData({
                  ...formData,
                  consultingEmail: e.target.value
                })} className="mt-1" data-id="element-1385" /> : <p className="font-medium text-slate-900 break-all" data-id="element-1386">
                      {client.consultingEmail}
                    </p>}
                </div>
              </div>
              {/* PEC & SDI */}
              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-50" data-id="element-1387">
                <div className="flex items-start gap-3" data-id="element-1388">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1389">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-1390" />
                  </div>
                  <div className="flex-1" data-id="element-1391">
                    <p className="text-xs text-slate-500" data-id="element-1392">PEC</p>
                    {isEditing ? <Input value={formData.pec} onChange={e => setFormData({
                    ...formData,
                    pec: e.target.value
                  })} className="mt-1" data-id="element-1393" /> : <p className="font-medium text-slate-900 break-all" data-id="element-1394">
                        {client.pec}
                      </p>}
                  </div>
                </div>
                <div data-id="element-1395">
                  <p className="text-xs text-slate-500" data-id="element-1396">SDI</p>
                  {isEditing ? <Input value={formData.sdi} onChange={e => setFormData({
                  ...formData,
                  sdi: e.target.value
                })} className="mt-1" data-id="element-1397" /> : <p className="font-medium text-slate-900" data-id="element-1398">{client.sdi}</p>}
                </div>
              </div>
              {/* Referenti Section */}
              <div className="pt-4 border-t border-slate-100" data-id="element-1399">
                <p className="text-xs font-bold text-seform-navy uppercase mb-3" data-id="element-1400">
                  Referenti
                </p>

                <div className="space-y-6" data-id="element-1401">
                  {/* Persona di riferimento & Cellulare - Side by side */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-1402">
                    <div data-id="element-1403">
                      <p className="text-xs text-slate-500" data-id="element-1404">
                        Persona di riferimento
                      </p>
                      {isEditing ? <Input value={formData.contactPerson} onChange={e => setFormData({
                      ...formData,
                      contactPerson: e.target.value
                    })} className="mt-1" data-id="element-1405" /> : <div className="flex items-center gap-2" data-id="element-1406">
                          <User className="h-3 w-3 text-slate-400" data-id="element-1407" />
                          <span className="font-medium text-slate-900" data-id="element-1408">
                            {client.contactPerson}
                          </span>
                        </div>}
                    </div>

                    <div data-id="element-1409">
                      <p className="text-xs text-slate-500" data-id="element-1410">
                        Cellulare referente
                      </p>
                      {isEditing ? <Input value={formData.contactMobile} onChange={e => setFormData({
                      ...formData,
                      contactMobile: e.target.value
                    })} className="mt-1" data-id="element-1411" /> : <div className="flex items-center gap-2" data-id="element-1412">
                          <Smartphone className="h-3 w-3 text-slate-400" data-id="element-1413" />
                          <span className="font-medium text-slate-900" data-id="element-1414">
                            {client.contactMobile}
                          </span>
                        </div>}
                    </div>
                  </div>

                  {/* Referente sicurezza & Cellulare - Side by side */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-1415">
                    <div data-id="element-1416">
                      <p className="text-xs text-slate-500" data-id="element-1417">
                        Referente sicurezza
                      </p>
                      {isEditing ? <Input value={formData.safetyReferent} onChange={e => setFormData({
                      ...formData,
                      safetyReferent: e.target.value
                    })} className="mt-1" data-id="element-1418" /> : <div className="flex items-center gap-2" data-id="element-1419">
                          <Shield className="h-3 w-3 text-slate-400" data-id="element-1420" />
                          <span className="font-medium text-slate-900" data-id="element-1421">
                            {client.safetyReferent}
                          </span>
                        </div>}
                    </div>

                    <div data-id="element-1422">
                      <p className="text-xs text-slate-500" data-id="element-1423">
                        Cellulare Ref. Sicurezza
                      </p>
                      {isEditing ? <Input value={formData.safetyMobile} onChange={e => setFormData({
                      ...formData,
                      safetyMobile: e.target.value
                    })} className="mt-1" data-id="element-1424" /> : <div className="flex items-center gap-2" data-id="element-1425">
                          <Smartphone className="h-3 w-3 text-slate-400" data-id="element-1426" />
                          <span className="font-medium text-slate-900" data-id="element-1427">
                            {client.safetyMobile || '-'}
                          </span>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>
              {/* Other Emails */}
              <div className="pt-4 border-t border-slate-100 space-y-5" data-id="element-1428">
                <div className="flex items-start gap-3" data-id="element-1429">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1430">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-1431" />
                  </div>
                  <div className="flex-1" data-id="element-1432">
                    <p className="text-xs text-slate-500" data-id="element-1433">Email formazione</p>
                    {isEditing ? <Input value={formData.trainingEmail} onChange={e => setFormData({
                    ...formData,
                    trainingEmail: e.target.value
                  })} className="mt-1" data-id="element-1434" /> : <p className="font-medium text-slate-900 break-all" data-id="element-1435">
                        {client.trainingEmail}
                      </p>}
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-1436">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1437">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-1438" />
                  </div>
                  <div className="flex-1" data-id="element-1439">
                    <p className="text-xs text-slate-500" data-id="element-1440">Email informative</p>
                    {isEditing ? <Input value={formData.infoEmail} onChange={e => setFormData({
                    ...formData,
                    infoEmail: e.target.value
                  })} className="mt-1" data-id="element-1441" /> : <p className="font-medium text-slate-900 break-all" data-id="element-1442">
                        {client.infoEmail}
                      </p>}
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-1443">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-1444">
                    <Mail className="h-4 w-4 text-slate-500" data-id="element-1445" />
                  </div>
                  <div className="flex-1" data-id="element-1446">
                    <p className="text-xs text-slate-500" data-id="element-1447">Email newsletter</p>
                    {isEditing ? <Input value={formData.newsletterEmail} onChange={e => setFormData({
                    ...formData,
                    newsletterEmail: e.target.value
                  })} className="mt-1" data-id="element-1448" /> : <p className="font-medium text-slate-900 break-all" data-id="element-1449">
                        {client.newsletterEmail}
                      </p>}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Ticket History Modal */}
      <Modal isOpen={showTicketHistory} onClose={() => setShowTicketHistory(false)} title="Storico Ticket" size="lg" data-id="element-1450">
        <div className="space-y-4" data-id="element-1451">
          {[1, 2, 3].map(i => <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100" data-id="element-1452">
              <div className="flex justify-between items-start mb-2" data-id="element-1453">
                <span className="font-mono text-xs text-slate-500" data-id="element-1454">
                  TKT-2024-{100 + i}
                </span>
                <Badge variant={i === 1 ? 'warning' : 'success'} data-id="element-1455">
                  {i === 1 ? 'In corso' : 'Chiuso'}
                </Badge>
              </div>
              <h4 className="font-medium text-slate-900 mb-1" data-id="element-1456">
                Richiesta assistenza tecnica {i}
              </h4>
              <p className="text-sm text-slate-600 mb-2" data-id="element-1457">
                Descrizione breve del problema riscontrato dal cliente...
              </p>
              <div className="flex items-center gap-4 text-xs text-slate-500" data-id="element-1458">
                <span className="flex items-center gap-1" data-id="element-1459">
                  <Clock className="h-3 w-3" data-id="element-1460" /> 12/05/2024
                </span>
                <span className="flex items-center gap-1" data-id="element-1461">
                  <User className="h-3 w-3" data-id="element-1462" /> Mario Rossi
                </span>
              </div>
            </div>)}
        </div>
        <div className="mt-6 flex justify-end" data-id="element-1463">
          <Button onClick={() => setShowTicketHistory(false)} data-id="element-1464">Chiudi</Button>
        </div>
      </Modal>

      {/* Nuova Offerta Modal (for pending clients) */}
      <Modal isOpen={showNewOfferModal} onClose={() => {
      setShowNewOfferModal(false);
      setOfferSaveSuccess(false);
    }} title="Nuova offerta" size="2xl" data-id="element-1465">
        <AnimatePresence mode="wait" data-id="element-1466">
          {offerSaveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-1467">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-1468">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-1469" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-1470">
                Offerta creata con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1471">
              <div className="space-y-8" data-id="element-1472">
                <section data-id="element-1473">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1474">
                    <FileText className="h-4 w-4" data-id="element-1475" /> Dati generali
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1476">
                    <Input label="Cliente" value={client?.name || ''} disabled data-id="element-1477" />
                    <Select label="Prodotto" options={[{
                  value: 'Consulenza Sicurezza',
                  label: 'Consulenza Sicurezza'
                }, {
                  value: 'HACCP Base',
                  label: 'HACCP Base'
                }, {
                  value: 'Privacy GDPR',
                  label: 'Privacy GDPR'
                }, {
                  value: 'Formazione Lavoratori',
                  label: 'Formazione Lavoratori'
                }, {
                  value: 'DVR',
                  label: 'DVR'
                }]} required data-id="element-1478" />
                    <Select label="Tipologia" options={[{
                  value: '',
                  label: 'Seleziona...'
                }, {
                  value: 'Sicurezza',
                  label: 'Sicurezza'
                }, {
                  value: 'Privacy',
                  label: 'Privacy'
                }, {
                  value: 'HACCP',
                  label: 'HACCP'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} data-id="element-1479" />
                    <Select label="Responsabile sviluppo" options={[{
                  value: 'alessio',
                  label: 'Alessio'
                }, {
                  value: 'gianfranco',
                  label: 'Gian Franco'
                }, {
                  value: 'lorenzo',
                  label: 'Lorenzo'
                }, {
                  value: 'tiziano',
                  label: 'Tiziano'
                }]} required data-id="element-1480" />
                    <Select label="Provenienza" options={[{
                  value: '',
                  label: 'Seleziona...'
                }, {
                  value: 'Commerciale SEF',
                  label: 'Commerciale SEF'
                }, {
                  value: 'Esterni e partner',
                  label: 'Esterni e partner'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} data-id="element-1481" />
                    <Input label="Corrispettivo provenienza (€)" placeholder="Es. 500,00" data-id="element-1482" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-1483"></div>

                <section data-id="element-1484">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-1485">
                    <Euro className="h-4 w-4" data-id="element-1486" /> Dettagli economici
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1487">
                    <Input label="Importo (€)" type="number" placeholder="0.00" required data-id="element-1488" />
                    <Input label="Durata (mesi)" type="number" placeholder="12" data-id="element-1489" />
                    <Select label="Rinnovo tacito" options={[{
                  value: 'si',
                  label: 'Sì'
                }, {
                  value: 'no',
                  label: 'No'
                }]} data-id="element-1490" />
                    <Input label="Scadenza" type="date" data-id="element-1491" />
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1492">
                  <Button variant="flat" onClick={() => setShowNewOfferModal(false)} data-id="element-1493">
                    Annulla
                  </Button>
                  <Button onClick={() => {
                setOfferSaveSuccess(true);
                setTimeout(() => {
                  setOfferSaveSuccess(false);
                  setShowNewOfferModal(false);
                }, 1500);
              }} data-id="element-1494">
                    Crea offerta
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}